import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import Cookies from 'universal-cookie';
import { useQueryParam, StringParam } from 'use-query-params';
import { readContract } from '@wagmi/core';
import MultiCallAbi from '../config/MultiCallAbi.json';
import LaunchpadCard from '../components/LaunchpadCard';
import ClipLoader from 'react-spinners/ClipLoader';
import TopBar from '../components/TopBar';
import { imageUrl, fetchBnbPrice, supportedChainIds, chainLogos, projectName } from '../utils/constants.ts';
import { getMulticallAddress } from '../utils/addressHelpers.ts';
import Slider from '../components/Slider.jsx';
import Web3 from 'web3';
import rot13 from '../utils/encode.ts';
import { config } from '../config.jsx'
import Footer from '../components/Footer';

const App = () => {
  let [loading, setLoading] = useState(false);
  const [funLists, setFunListData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalLength, setTotalLength] = useState(0);
  const [allLists, setAllLists] = useState([]);
  const cookies = new Cookies();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  let [ref] = useQueryParam('ref', StringParam);

  if (ref) {
    if (Web3.utils.isAddress(rot13(ref))) {
      cookies.set('ref', ref);
    }
  }

  useEffect(() => {
    const FetchData = async () => {
      try {
        setLoading(true);

        for (const chainId of supportedChainIds) {
          const ethPrice = await fetchBnbPrice();
          const mainInfo = await readContract(config, {
            address: getMulticallAddress(chainId),
            abi: MultiCallAbi,
            functionName: 'getMainInfo',
            chainId: chainId,
          });

          const otherInfo = await readContract(config, {
            address: getMulticallAddress(chainId),
            abi: MultiCallAbi,
            functionName: 'getOtherInfo',
            chainId: chainId,
          });

          const restInfo = await readContract(config, {
            address: getMulticallAddress(chainId),
            abi: MultiCallAbi,
            functionName: 'getRestInfo',
            chainId: chainId,
          });

          if (mainInfo[0].length > 0) {
            for (let i = mainInfo[0].length - 1; i >= 0; i--) {
              let startTime = Number(mainInfo[0][i]);
              const lpCreated = restInfo[1][i];
              let progress;
              const contractAddress = mainInfo[5][i];
              const virtualLpAmounts = Number(mainInfo[2][i]) * ethPrice;
              const tokenPrice = Number(mainInfo[3][i]);
              const marketCap = 4000 + (tokenPrice * 1000000000 * Number(ethPrice)) / 10 ** 12;
              const website = otherInfo[2][i];
              const twitter = otherInfo[3][i];
              const telegram = otherInfo[4][i];
              progress = lpCreated ? 100 : (marketCap * 100) / 50000;
              const liquidity = virtualLpAmounts * 2;
              const name = otherInfo[0][i];
              const symbol = otherInfo[1][i];
              const description = restInfo[2][i];
              const logoUrl = imageUrl + 'logo/' + projectName + "-" + chainId + "-" + contractAddress + '.png';
              const bannerUrl = imageUrl + 'banner/' + projectName + "-" + chainId + "-" + contractAddress + '.png';
              let blockchainLogoUrl = chainLogos[chainId];
              let devAddress = restInfo[4][i];
              let raisingPercent = Number(otherInfo[5][i]) / 100;
              let dexAddress = restInfo[3][i];
              let dexName = 'PancakeSwap';
              const funData = {
                chainId,
                startTime: startTime,
                progress: progress,
                Liquidity: liquidity,
                tokenName: name,
                tokenSymbol: symbol,
                tokenDescription: description,
                logoUrl: logoUrl,
                bannerUrl: bannerUrl,
                address: mainInfo[5][i],
                depositedAmount: (Number(mainInfo[4][i]) * ethPrice) / 10 ** 18,
                contractAddress: contractAddress,
                dexAddress: dexAddress,
                devAddress: devAddress,
                dexName: dexName,
                marketCap: marketCap,
                website: website,
                twitter: twitter,
                telegram: telegram,
                blockchainLogoUrl: blockchainLogoUrl,
                raisingPercent: raisingPercent,
                lpCreated,
              };
              setFunListData((prevState) => [...prevState, funData]);
            }
          }
        }

        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.error(e);
      }
    };
    FetchData();
  }, []);

  const sortOptions = [
    { value: 'Creation Time', label: 'Creation Time' },
    { value: 'Market Cap', label: 'Market Cap' },
    { value: 'Volume', label: 'Volume' },
  ];

  const orderOptions = [
    { value: 'Descending', label: 'Descending' },
    { value: 'Ascending', label: 'Ascending' },
  ];

  const statusOptions = [
    { value: 'All', label: 'All' },
    { value: 'Listed', label: 'Listed' },
    { value: 'Live', label: 'Live' },
  ];

  const chainOptions = [
    { value: 'All Chains', label: 'All Chains' },
    { value: 'BSC', label: 'BSC' },
  ];

  function FilterSelect({ options, defaultValue, onChange }) {
    const handleChange = (newValue) => {
      onChange(newValue);
    };

    return (
      <Select
        defaultValue={defaultValue}
        isSearchable={false}
        options={options}
        value={options.find((option) => option.value === defaultValue.value)}
        onChange={handleChange}
        styles={{
          control: (styles) => ({
            ...styles,
            padding: '4px 0px',
            background: 'var(--button-bg)',
            border: '1px solid var(--dropdown-border)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            borderRadius: '10px',
            cursor: 'pointer',
            outline: 'none',
          }),
          option: (styles, { isFocused, isSelected }) => ({
            ...styles,
            background: isFocused
              ? isSelected
                ? 'var(--dropdown-hover)'
                : 'var(--dropdown-hover-bg)'
              : 'var(--dropdown-bg)',
            color: 'var(--dropdown-item-text)',
            cursor: 'pointer',
            zIndex: '9999',
          }),
          singleValue: (styles) => ({
            ...styles,
            color: 'var(--button-text)',
            outline: 'none',
          }),
          indicatorSeparator: (styles) => ({
            ...styles,
            display: 'none',
          }),
          dropdownIndicator: (styles) => ({
            ...styles,
            color: 'var(--button-text)',
          }),
          menuList: (styles) => ({
            ...styles,
            background: 'var(--dropdown-bg)',
            borderRadius: '5px',
            border: '1px solid var(--dropdown-border)',
          }),
        }}
      />
    );
  }

  const [sortedFunLists, setSortedFunLists] = useState([]);
  const [filteredFunLists, setFilteredFunLists] = useState([]);
  const [sortValue, setSortValue] = useState(sortOptions[0]);
  const [orderValue, setOrderValue] = useState(orderOptions[0]);
  const [statusValue, setStatusValue] = useState(statusOptions[0]);
  const [chainValue, setChainValue] = useState(chainOptions[0]);

  const filterFunLists = useCallback(() => {
    const searchFiltered = funLists.filter((list) => {
      if (!search || search === '') {
        return true;
      }

      const searchLower = search.toLowerCase();

      if (list.tokenName.toLowerCase().includes(searchLower)) {
        return true;
      }
      return false;
    });
    let chainFilteredList = [];
    switch (chainValue.value) {
      case 'All Chains':
        chainFilteredList = [...searchFiltered];
        break;
      case 'BSC':
        chainFilteredList = [...searchFiltered.filter((item) => item.chainId === 56)];
        break;
      default:
        break;
    }
    let filteredList = [];
    switch (statusValue.value) {
      case 'All':
        filteredList = [...chainFilteredList];
        break;
      case 'Listed':
        filteredList = [...chainFilteredList.filter((item) => item.progress >= 100)];
        break;
      case 'Live':
        filteredList = [...chainFilteredList.filter((item) => item.progress < 100)];
        break;
      default:
        break;
    }
    setFilteredFunLists(filteredList);
  }, [statusValue, chainValue, funLists, search]);

  const onSearchChanged = (event) => {
    setSearch(event.target.value);
  };

  const sortFunLists = useCallback(async () => {
    let sortedList = [];
    switch (sortValue.value) {
      case 'Market Cap':
        sortedList = [...filteredFunLists].sort((a, b) => {
          if (orderValue.value === 'Ascending') {
            return a.marketCap - b.marketCap;
          } else {
            return b.marketCap - a.marketCap;
          }
        });
        break;
      case 'Creation Time':
        sortedList = [...filteredFunLists].sort((a, b) => {
          if (orderValue.value === 'Ascending') {
            return a.startTime - b.startTime;
          } else {
            return b.startTime - a.startTime;
          }
        });
        break;
      case 'Volume':
        sortedList = [...filteredFunLists].sort((a, b) => {
          if (orderValue.value === 'Ascending') {
            return a.depositedAmount - b.depositedAmount;
          } else {
            return b.depositedAmount - a.depositedAmount;
          }
        });
        break;
      default:
        break;
    }
    setAllLists(sortedList);
    let last;
    let length = sortedList.length;
    setTotalLength(length);
    let datas = [];
    if (length > 12) {
      last = 12;
    } else {
      last = length;
    }
    for (let i = 0; i < last; i++) {
      datas.push(sortedList[i]);
    }
    setCurrentIndex(last);
    setSortedFunLists(datas);
  }, [orderValue, sortValue, filteredFunLists]);

  useEffect(() => {
    setFilteredFunLists([...funLists]);
  }, [funLists, statusValue]);

  useEffect(() => {
    filterFunLists();
  }, [statusValue, filterFunLists]);

  useEffect(() => {
    sortFunLists();
  }, [orderValue, sortValue, filteredFunLists, sortFunLists]);

  const onSortChange = (newValue) => {
    setSortValue(newValue);
    sortFunLists();
  };

  const onChainChange = (val) => {
    setChainValue(val);
    filterFunLists();
  };

  const onOrderChange = () => {
    if (orderValue.value === 'Descending') {
      setOrderValue(orderOptions[1]);
    } else {
      setOrderValue(orderOptions[0]);
    }
    sortFunLists();
  };

  const onStatusChange = (newValue) => {
    setStatusValue(newValue);
    filterFunLists();
  };

  const loadMoreLists = () => async () => {
    try {
      setLoading(true);
      let newLists = [];
      let last;
      const length = totalLength - currentIndex;
      if (length >= 0) {
        if (length > 12) {
          last = currentIndex + 12;
        } else {
          last = currentIndex + length;
        }
        for (let i = currentIndex; i < last; i++) {
          newLists.push(allLists[i]);
        }
        let newDatas = sortedFunLists.concat(newLists);
        setSortedFunLists(newDatas);
        setCurrentIndex(last);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  };

  const LaunchpadCardGrid = ({ items }) => {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map(
          (
            {
              chainId,
              progress,
              Liquidity,
              tokenName,
              tokenDescription,
              logoUrl,
              bannerUrl,
              address,
              depositedAmount,
              contractAddress,
              dexAddress,
              devAddress,
              dexName,
              marketCap,
              website,
              twitter,
              telegram,
              blockchainLogoUrl,
              raisingPercent,
            },
            i
          ) => (
            <LaunchpadCard
              key={`${contractAddress}-${i}`}
              chainId={chainId}
              progress={progress}
              Liquidity={Liquidity}
              tokenName={tokenName}
              tokenDescription={tokenDescription}
              logoUrl={logoUrl}
              bannerUrl={bannerUrl}
              funAddress={address}
              depositedAmount={depositedAmount}
              contractAddress={contractAddress}
              dexAddress={dexAddress}
              devAddress={devAddress}
              dexName={dexName}
              marketCap={marketCap}
              website={website}
              twitter={twitter}
              telegram={telegram}
              blockchainLogoUrl={blockchainLogoUrl}
              raisingPercent={raisingPercent}
            />
          )
        )}
      </div>
    );
  };

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
    cookies.set('show-how-it-works', 'true');
  };

  const closeModal = (e) => {
    if (e.target.id === 'modal') {
      setModalIsOpen(false);
      cookies.set('show-how-it-works', 'true');
    }
  };

  const modalContent = (
    <div id="modal" onClick={closeModal} className={`modal ${modalIsOpen ? 'show-modal' : ''}`}>
      <div className="rounded-xl p-6 max-w-md" style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow)'
      }}>
        <h1 className="text-center text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          All created tokens in DogWifHair Fun are safe!
        </h1>
        <ul className="space-y-2 mb-4" style={{ color: 'var(--text-secondary)' }}>
          <li>✅ Each coin is a fair-launch with no presale and no team allocation.</li>
          <li>✅ Everyone has equal chance!</li>
        </ul>
        <h1 className="text-center text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>How it works</h1>
        <ol className="space-y-2 mb-4" style={{ color: 'var(--card-text-secondary)' }}>
          <li>1️⃣ Pick a coin that you like</li>
          <li>2️⃣ Buy the coin on the bonding curve</li>
          <li>3️⃣ Sell at any time to lock in your profits or losses</li>
          <li>4️⃣ When enough people buy, it reaches $50k market cap and gets listed on DEX</li>
          <li>5️⃣ LP Tokens will be burned🔥</li>
        </ol>
        <p className="italic text-sm mb-4" style={{ color: 'var(--card-text-secondary)' }}>
          Disclaimer: Trading tokens is highly risky / speculative. Do not invest more than you can afford to lose.
        </p>
        <div className="text-center">
          <button
            onClick={toggleModal}
            className="px-6 py-3 rounded-lg font-medium transition-all"
            style={{
              background: 'var(--button-bg)',
              color: 'var(--button-text)',
            }}
          >
            I'm ready to pump
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen" style={{ background: 'var(--page-bg)' }}>
      <div className="flex-grow">
        <style jsx>{`
        .modal {
          opacity: 0;
          visibility: hidden;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          transition: all 0.3s ease;
          z-index: 100;
        }

        .show-modal {
          opacity: 1;
          visibility: visible;
        }
      `}</style>
        {modalContent}
        <TopBar />
        <div className="max-w-7xl mx-auto pt-28 pb-16 px-4 sm:px-6">
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Link
              to="/create"
              className="px-6 py-3 text-center rounded-lg font-bold transition-all shadow-lg hover:shadow-xl"
              style={{
                background: 'var(--button-bg)',
                color: 'var(--button-text)',
                minWidth: '200px'
              }}
            >
              Create Token
            </Link>
            <a
              href="https://pancakeswap.finance/swap"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 text-center rounded-lg font-bold transition-all shadow-lg hover:shadow-xl"
              style={{
                background: 'var(--button-bg)',
                color: 'var(--button-text)',
                minWidth: '200px'
              }}
            >
              DEX
            </a>
          </div>

          {/* Market Stats Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            {/* Market Cap Card */}
            <div className="lg:p-6 rounded-xl p-1" style={{
              background: 'var(--card-bg)',
              border: '1px solid transparent',
              backgroundImage: 'linear-gradient(var(--card-bg), var(--card-bg)), var(--card-border)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              boxShadow: 'var(--shadow)'
            }}>
              <h2 className="text-center text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Market Cap
              </h2>
              <div className="overflow-x-auto">
                <div className="grid grid-cols-6 gap-2 text-sm mb-2" style={{ color: 'var(--table-header-text)' }}>
                  <div className="text-center">Rank</div>
                  <div className="text-center col-span-2">Token Name</div>
                  <div className="text-center">MarketCap</div>
                  <div className="text-center col-span-2">Progress</div>
                </div>
                <div className="space-y-3 max-h-[268px] overflow-y-auto no-scrollbar">
                  {loading ? (
                    <div className="flex justify-center py-4">
                      <ClipLoader color="var(--text-accent)" size={30} />
                    </div>
                  ) : funLists?.length === 0 ? (
                    <div className="p-4 rounded-lg text-center" style={{ background: 'var(--table-item-bg)', color: 'var(--table-text)' }}>
                      No Data
                    </div>
                  ) : (
                    funLists
                      .sort((a, b) => b.marketCap - a.marketCap)
                      .slice(0, 10)
                      .map((pool, index) => (
                        <div className='h-14'>
                          <Link key={index} to={`/buy/?chain=${pool.chainId}&address=${pool.address}`}>
                            <div className="grid grid-cols-6 gap-2 items-center lg:p-3 rounded-lg hover:bg-opacity-90 transition-colors p-1" style={{ background: 'var(--table-item-bg)' }}>
                              <div className="text-center font-semibold" style={{ color: 'var(--table-text)' }}>{index + 1}</div>
                              <div className="col-span-2 flex items-center gap-2">
                                <img
                                  src={pool.logoUrl}
                                  alt={pool.tokenName}
                                  className="w-8 h-8 rounded-full"
                                  onError={e => {
                                    e.target.onerror = null;
                                    e.target.src = '/logo.png';
                                  }}
                                />
                                <span className="truncate" style={{ color: 'var(--table-text)' }}>{pool.tokenName}</span>
                              </div>
                              <div className="text-center font-medium truncate" style={{ color: 'var(--table-text)' }}>${(pool.marketCap).toFixed(2)}</div>
                              <div className="col-span-2">
                                <div className="text-center text-sm mb-1 font-medium" style={{ color: 'var(--table-text)' }}>
                                  {Number(pool.progress > 100 ? 100 : pool.progress).toFixed(2)}%
                                </div>
                                <div className="w-full h-1.5 rounded-full" style={{ background: 'var(--progress-container-bg)' }}>
                                  <div
                                    className="h-full rounded-full"
                                    style={{
                                      width: `${pool.progress > 100 ? 100 : pool.progress}%`,
                                      background: 'var(--progress-fill)'
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </Link>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>

            {/* Gainers Card */}
            <div className="lg:p-6 rounded-xl p-1" style={{
              background: 'var(--card-bg)',
              border: '1px solid transparent',
              backgroundImage: 'linear-gradient(var(--card-bg), var(--card-bg)), var(--card-border)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              boxShadow: 'var(--shadow)'
            }}>
              <h2 className="text-center text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                24h Gainers
              </h2>
              <div className="overflow-x-auto">
                <div className="grid grid-cols-6 gap-2 text-sm mb-2" style={{ color: 'var(--table-header-text)' }}>
                  <div className="text-center">Rank</div>
                  <div className="text-center col-span-2">Token Name</div>
                  <div className="text-center">MarketCap</div>
                  <div className="text-center col-span-2">Rise</div>
                </div>
                <div className="space-y-3 max-h-[268px] overflow-y-auto no-scrollbar">
                  {loading ? (
                    <div className="flex justify-center py-4">
                      <ClipLoader color="var(--text-accent)" size={30} />
                    </div>
                  ) : funLists?.length === 0 ? (
                    <div className="p-4 rounded-lg text-center" style={{ background: 'var(--table-item-bg)', color: 'var(--table-text)' }}>
                      No Data
                    </div>
                  ) : (
                    funLists
                      .sort((a, b) => b.raisingPercent - a.raisingPercent)
                      .slice(0, 10)
                      .map((pool, index) => (
                        <div className='h-14'>
                          <Link key={index} to={`/buy/?chain=${pool.chainId}&address=${pool.address}`}>
                            <div className="grid grid-cols-6 gap-2 items-center lg:p-3 rounded-lg hover:bg-opacity-90 transition-colors p-1" style={{ background: 'var(--table-item-bg)' }}>
                              <div className="text-center font-semibold" style={{ color: 'var(--table-text)' }}>{index + 1}</div>
                              <div className="col-span-2 flex items-center gap-2">
                                <img
                                  src={pool.logoUrl}
                                  alt={pool.tokenName}
                                  className="w-8 h-8 rounded-full"
                                  onError={e => {
                                    e.target.onerror = null;
                                    e.target.src = '/logo.png';
                                  }}
                                />
                                <span className="truncate" style={{ color: 'var(--table-text)' }}>{pool.tokenName}</span>
                              </div>
                              <div className="text-center font-medium truncate" style={{ color: 'var(--table-text)' }}>${((pool.marketCap).toFixed(2))}</div>
                              <div className="col-span-2 flex items-center justify-center font-medium" style={{ color: 'var(--table-text)' }}>
                                {(pool.raisingPercent).toFixed(2)}%
                                <svg fill="currentColor" viewBox="0 0 11 18" width="11" className="ml-1" style={{ color: 'var(--table-text)' }}>
                                  <g>
                                    <path d="M3.21533 7.62257V16.9824C3.21533 17.5347 3.64908 17.9998 4.34309 17.9998H6.48294C7.03236 17.9998 7.6107 17.5638 7.6107 16.9824V7.62257H9.75056C10.994 7.62257 11.341 6.80866 10.647 5.90756L6.80103 0.67534C6.13594 -0.225764 4.97926 -0.225764 4.1985 0.67534L0.352553 5.90756C-0.341453 6.83773 0.00554999 7.62257 1.24898 7.62257H3.21533Z"></path>
                                  </g>
                                </svg>
                              </div>
                            </div>
                          </Link>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Listed Projects */}
          {funLists.filter((item) => item.progress >= 100).length > 0 && (
            <div className="mb-12">
              <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Listed Projects</h2>
              <Slider items={funLists.filter((item) => item.progress >= 100)} isListed={true} />
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center mb-8">
            <div className="flex flex-wrap gap-2">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${sortValue.value === option.value
                    ? 'text-white'
                    : 'text-gray-500'
                    }`}
                  onClick={() => onSortChange(option)}
                  style={{
                    background: sortValue.value === option.value
                      ? 'var(--button-bg)'
                      : 'var(--button-secondary-bg)',
                    border: sortValue.value === option.value
                      ? 'none'
                      : '1px solid var(--button-secondary-border)',
                    color: sortValue.value === option.value
                      ? 'var(--button-text)'
                      : 'var(--button-secondary-text)'
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-4 w-full lg:w-auto lg:flex-row">
              <div className="relative w-full lg:w-64">
                <input
                  className="w-full p-3 pl-10 rounded-lg focus:outline-none"
                  placeholder="Search Token"
                  onChange={onSearchChanged}
                  style={{
                    background: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    color: 'var(--input-color)'
                  }}
                />
                <svg
                  className="absolute left-3 top-3.5 w-5 h-5"
                  style={{ color: 'var(--text-secondary)' }}
                  fill="currentColor"
                  viewBox="0 0 18 18"
                >
                  <g>
                    <path d="M7.75987 15.5197C3.48078 15.5197 0 12.039 0 7.75987C0 3.48078 3.48078 0 7.75987 0C12.039 0 15.5197 3.48078 15.5197 7.75987C15.5197 12.039 12.039 15.5197 7.75987 15.5197ZM7.75987 1.90911C4.53381 1.90911 1.90911 4.53381 1.90911 7.75855C1.90911 10.9833 4.53381 13.608 7.75987 13.608C10.9859 13.608 13.6106 10.9833 13.6106 7.75855C13.6106 4.53381 10.9859 1.91042 7.75987 1.91042V1.90911Z"></path>
                    <path d="M16.8235 17.9987C16.5228 17.9987 16.2221 17.8845 15.9924 17.6547L11.9378 13.6001C11.4783 13.1406 11.4783 12.3974 11.9378 11.9379C12.3974 11.4783 13.1418 11.4783 13.6001 11.9379L17.6546 15.9924C18.1142 16.452 18.1142 17.1951 17.6546 17.6547C17.4249 17.8845 17.1242 17.9987 16.8235 17.9987Z"></path>
                  </g>
                </svg>
              </div>

              <div className="flex gap-2">
                <FilterSelect options={statusOptions} defaultValue={statusValue} onChange={onStatusChange} />
                <FilterSelect options={chainOptions} defaultValue={chainValue} onChange={onChainChange} />
                <button
                  className="p-2 rounded-lg flex items-center justify-center"
                  onClick={onOrderChange}
                  style={{
                    background: 'var(--button-secondary-bg)',
                    border: '1px solid var(--button-secondary-border)',
                    color: 'var(--button-secondary-text)',
                    minWidth: '44px',
                    maxHeight: '46px'
                  }}
                >
                  {orderValue.value === 'Ascending' ? (
                    <svg fill="currentColor" viewBox="0 0 21 17" width="21">
                      <g>
                        <path d="M17.2974 12.6684L19.0757 10.8901C19.5165 10.4493 20.2292 10.4493 20.67 10.8901C21.1109 11.331 21.1109 12.0437 20.67 12.4845L17.0607 16.0925C16.9025 16.2953 16.6807 16.4387 16.4306 16.4982C15.8274 16.651 15.2148 16.2872 15.062 15.6841C15.0228 15.5299 15.0174 15.3703 15.0444 15.2135V1.12783C15.0444 0.505763 15.5488 0 16.1723 0C16.7957 0 17.3001 0.504411 17.3001 1.12783V12.6684H17.2974ZM1.12783 1.45373H10.8942C11.5163 1.45373 12.022 1.95814 12.022 2.58156C12.022 3.20497 11.5176 3.70938 10.8942 3.70938H1.12783C0.504411 3.70803 0 3.20362 0 2.5802C0 1.95679 0.504411 1.45373 1.12783 1.45373ZM1.12783 7.46474H10.8942C11.5163 7.44446 12.0383 7.93264 12.0585 8.5547C12.0788 9.17677 11.5906 9.69876 10.9686 9.71904C10.9442 9.71904 10.9185 9.71904 10.8942 9.71904H1.12783C0.505763 9.71904 0 9.21463 0 8.59122C0 7.9678 0.504411 7.46339 1.12783 7.46339V7.46474ZM1.12783 13.4758H10.8942C11.5163 13.4758 12.022 13.9802 12.022 14.6036C12.022 15.227 11.5176 15.7314 10.8942 15.7314H1.12783C0.505763 15.7314 0 15.227 0 14.6036C0 13.9802 0.504411 13.4758 1.12783 13.4758Z"></path>
                      </g>
                    </svg>
                  ) : (
                    <svg fill="currentColor" viewBox="0 0 21 17" width="21" style={{ transform: 'rotate(180deg)' }}>
                      <g>
                        <path d="M17.2974 12.6684L19.0757 10.8901C19.5165 10.4493 20.2292 10.4493 20.67 10.8901C21.1109 11.331 21.1109 12.0437 20.67 12.4845L17.0607 16.0925C16.9025 16.2953 16.6807 16.4387 16.4306 16.4982C15.8274 16.651 15.2148 16.2872 15.062 15.6841C15.0228 15.5299 15.0174 15.3703 15.0444 15.2135V1.12783C15.0444 0.505763 15.5488 0 16.1723 0C16.7957 0 17.3001 0.504411 17.3001 1.12783V12.6684H17.2974ZM1.12783 1.45373H10.8942C11.5163 1.45373 12.022 1.95814 12.022 2.58156C12.022 3.20497 11.5176 3.70938 10.8942 3.70938H1.12783C0.504411 3.70803 0 3.20362 0 2.5802C0 1.95679 0.504411 1.45373 1.12783 1.45373ZM1.12783 7.46474H10.8942C11.5163 7.44446 12.0383 7.93264 12.0585 8.5547C12.0788 9.17677 11.5906 9.69876 10.9686 9.71904C10.9442 9.71904 10.9185 9.71904 10.8942 9.71904H1.12783C0.505763 9.71904 0 9.21463 0 8.59122C0 7.9678 0.504411 7.46339 1.12783 7.46339V7.46474ZM1.12783 13.4758H10.8942C11.5163 13.4758 12.022 13.9802 12.022 14.6036C12.022 15.227 11.5176 15.7314 10.8942 15.7314H1.12783C0.505763 15.7314 0 15.227 0 14.6036C0 13.9802 0.504411 13.4758 1.12783 13.4758Z"></path>
                      </g>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Projects Grid */}
          {sortedFunLists.length > 0 ? (
            <>
              <LaunchpadCardGrid items={sortedFunLists} />
              {loading ? (
                <div className="flex justify-center py-8">
                  <ClipLoader color="var(--text-accent)" size={50} />
                </div>
              ) : totalLength - currentIndex > 0 ? (
                <div className="text-center mt-8">
                  <button
                    onClick={loadMoreLists()}
                    className="px-6 py-3 rounded-lg font-medium transition-all"
                    style={{
                      background: 'var(--button-secondary-bg)',
                      border: '1px solid var(--button-secondary-border)',
                      color: 'var(--button-secondary-text)'
                    }}
                  >
                    Load more...
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <div className="text-center py-12" style={{ color: 'var(--text-secondary)' }}>
              No projects found
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default App;
